import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  id: '',
  name: '',
  location: '',
  date: '',
  start_time: '',
  end_time: '',
  notes: '',
};

function formatDateUI(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function MarketsAdminPage() {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | empty | existing
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const isNew = editing && !editing.id;

  const fetchMarkets = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/markets`, {
        params: { upcoming_only: false },
      });
      setMarkets(r.data || []);
    } catch (_e) {
      toast.error('Failed to load markets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarkets();
  }, []);

  const handleSave = async () => {
    if (!editing.name.trim() || !editing.location.trim() || !editing.date) {
      toast.error('Name, location and date are required');
      return;
    }
    const payload = {
      name: editing.name.trim(),
      location: editing.location.trim(),
      date: editing.date,
      start_time: editing.start_time || null,
      end_time: editing.end_time || null,
      notes: editing.notes || null,
    };
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/markets`, payload);
        toast.success('Market added');
      } else {
        await axios.put(`${API}/markets/${editing.id}`, payload);
        toast.success('Market updated');
      }
      setEditing(null);
      fetchMarkets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/markets/${deleteId}`);
      toast.success('Market removed');
      setDeleteId(null);
      fetchMarkets();
    } catch (_e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="pt-28 pb-16 min-h-screen bg-[#F5F5F0]" data-testid="markets-admin">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <AdminBreadcrumb current="Markets" />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-caption text-[#84A98C] mb-2">Admin</p>
            <h1 className="heading-display text-3xl text-[#354F52]">Markets Calendar</h1>
            <p className="text-sm text-[#84A98C] mt-1">
              {markets.length} market{markets.length === 1 ? '' : 's'} scheduled
            </p>
          </div>
          <Button
            onClick={() => setEditing({ ...empty })}
            className="btn-primary flex items-center gap-2"
            data-testid="new-market-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            New Market
          </Button>
        </div>

        <div className="bg-[#FAF9F6] rounded-sm shadow-soft">
          {loading ? (
            <div className="p-8 text-center text-[#84A98C]">Loading...</div>
          ) : markets.length === 0 ? (
            <div className="p-10 text-center">
              <Calendar className="w-10 h-10 text-[#84A98C] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#52796F] mb-4">No markets scheduled yet</p>
              <Button onClick={() => setEditing({ ...empty })} className="btn-secondary">
                Add Your First Market
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#CAD2C5]/30">
              {markets.map((m) => (
                <div
                  key={m.id}
                  className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
                  data-testid={`market-row-${m.id}`}
                >
                  <div className="text-sm text-[#52796F] font-medium sm:w-48 shrink-0">
                    {formatDateUI(m.date)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-[#354F52]">{m.name}</p>
                    <p className="text-xs text-[#84A98C] mt-0.5">
                      {m.location}
                      {m.start_time || m.end_time
                        ? ` · ${m.start_time || ''}${m.start_time && m.end_time ? ' – ' : ''}${m.end_time || ''}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing({ ...m })}
                      data-testid={`edit-market-${m.id}`}
                    >
                      <Edit className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(m.id)}
                      className="text-[#BC4749]"
                      data-testid={`delete-market-${m.id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#354F52]">
                {isNew ? 'New Market' : 'Edit Market'}
              </DialogTitle>
            </DialogHeader>
            {editing && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="m-name">Market name *</Label>
                  <Input
                    id="m-name"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Paphos Old Town Market"
                    data-testid="market-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m-loc">Location *</Label>
                  <Input
                    id="m-loc"
                    value={editing.location}
                    onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                    placeholder="Old Town Square, Paphos"
                    data-testid="market-location"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 sm:col-span-1">
                    <Label htmlFor="m-date">Date *</Label>
                    <Input
                      id="m-date"
                      type="date"
                      value={editing.date}
                      onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                      data-testid="market-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="m-start">Start</Label>
                    <Input
                      id="m-start"
                      type="time"
                      value={editing.start_time || ''}
                      onChange={(e) => setEditing({ ...editing, start_time: e.target.value })}
                      data-testid="market-start"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="m-end">End</Label>
                    <Input
                      id="m-end"
                      type="time"
                      value={editing.end_time || ''}
                      onChange={(e) => setEditing({ ...editing, end_time: e.target.value })}
                      data-testid="market-end"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m-notes">Notes (optional)</Label>
                  <Textarea
                    id="m-notes"
                    rows={2}
                    value={editing.notes || ''}
                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                    placeholder="Stall #14, look for the sage green canopy"
                    data-testid="market-notes"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-market">
                    {saving ? 'Saving...' : isNew ? 'Add market' : 'Save changes'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this market?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove the market from your calendar.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-[#BC4749] hover:bg-[#BC4749]/90"
                data-testid="confirm-delete-market"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
